<script setup>
import ButtonV4 from 'next/button/Button.vue';
import BillingCard from './BillingCard.vue';

defineProps({
  packages: { type: Array, default: () => [] },
  topups: { type: Array, default: () => [] },
});

const emit = defineEmits(['select']);

const formatNpr = amount =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'NPR',
    maximumFractionDigits: 0,
  }).format(amount);
</script>

<template>
  <BillingCard
    :title="$t('BILLING_SETTINGS.PACKAGES.TITLE')"
    :description="$t('BILLING_SETTINGS.PACKAGES.DESCRIPTION')"
  >
    <div class="grid gap-4 px-5 sm:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="pkg in packages"
        :key="pkg.id"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div class="flex items-baseline justify-between gap-2">
          <span class="text-base font-medium text-n-slate-12">
            {{ pkg.name }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              $t('BILLING_SETTINGS.PACKAGES.PER_MONTH', {
                price: formatNpr(pkg.prices.npr || 0),
              })
            }}
          </span>
        </div>
        <ul class="flex flex-col gap-1 text-sm text-n-slate-11">
          <li v-if="pkg.limits.agents">
            {{
              $t('BILLING_SETTINGS.PACKAGES.AGENTS', {
                count: pkg.limits.agents,
              })
            }}
          </li>
          <li>
            {{
              pkg.limits.inboxes
                ? $t('BILLING_SETTINGS.PACKAGES.INBOXES', {
                    count: pkg.limits.inboxes,
                  })
                : $t('BILLING_SETTINGS.PACKAGES.UNLIMITED_INBOXES')
            }}
          </li>
          <li v-if="pkg.limits.ai_credits">
            {{
              $t('BILLING_SETTINGS.PACKAGES.CREDITS', {
                count: pkg.limits.ai_credits,
              })
            }}
          </li>
        </ul>
        <ButtonV4
          sm
          solid
          blue
          class="mt-auto"
          :disabled="pkg.current"
          @click="
            emit('select', {
              kind: 'subscription',
              planId: pkg.id,
              name: pkg.name,
              amount: pkg.prices.npr || 0,
            })
          "
        >
          {{
            pkg.current
              ? $t('BILLING_SETTINGS.PACKAGES.CURRENT')
              : $t('BILLING_SETTINGS.PACKAGES.CHOOSE')
          }}
        </ButtonV4>
      </div>
    </div>

    <template v-if="topups.length">
      <div class="px-5 pt-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('BILLING_SETTINGS.PACKAGES.TOPUP_TITLE') }}
        </h4>
        <p class="text-sm text-n-slate-11">
          {{ $t('BILLING_SETTINGS.PACKAGES.TOPUP_DESCRIPTION') }}
        </p>
      </div>
      <div class="grid gap-4 px-5 sm:grid-cols-3">
        <div
          v-for="topup in topups"
          :key="topup.credits"
          class="flex items-center justify-between gap-2 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                $t('BILLING_SETTINGS.PACKAGES.TOPUP_CREDITS', {
                  count: topup.credits,
                })
              }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ formatNpr(topup.price_npr) }}
            </span>
          </div>
          <ButtonV4
            sm
            faded
            slate
            @click="
              emit('select', {
                kind: 'topup',
                credits: topup.credits,
                name: $t('BILLING_SETTINGS.PACKAGES.TOPUP_CREDITS', {
                  count: topup.credits,
                }),
                amount: topup.price_npr,
              })
            "
          >
            {{ $t('BILLING_SETTINGS.PACKAGES.CHOOSE') }}
          </ButtonV4>
        </div>
      </div>
    </template>
  </BillingCard>
</template>
